import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, UserCheck, Mail, Shield } from 'lucide-react';

export interface DeletedUserInfo {
  userId: string;
  email: string;
  originalRole: string;
  firstName?: string;
  lastName?: string;
  deletedAt?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface RestoreUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userInfo: DeletedUserInfo;
  onRestore: () => Promise<void>;
  isLoading?: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown date';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatRole = (role: string): string => {
  const roleNames: Record<string, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrator',
    operator: 'Operator',
    staff: 'Staff',
  };
  return roleNames[role] || role;
};

/**
 * RestoreUserDialog Component
 *
 * Dialog shown when attempting to invite a user whose email belongs to a deleted user.
 * Offers to restore the deleted user instead of creating a new one.
 */
export const RestoreUserDialog: React.FC<RestoreUserDialogProps> = ({
  open,
  onOpenChange,
  userInfo,
  onRestore,
  isLoading = false,
}) => {
  const handleRestore = async () => {
    try {
      await onRestore();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Failed to restore user:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const userName = userInfo.firstName && userInfo.lastName
    ? `${userInfo.firstName} ${userInfo.lastName}`
    : userInfo.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            User Previously Deleted
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            A user with this email was previously deleted. Would you like to restore their account?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">{userName}</p>
                <p className="text-sm text-slate-500">{userInfo.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-sm text-slate-600">Original Role</p>
                <p className="font-medium text-slate-900">{formatRole(userInfo.originalRole)}</p>
              </div>
            </div>

            {userInfo.organizationName && (
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-slate-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-sm text-slate-600">Organization</p>
                  <p className="font-medium text-slate-900">{userInfo.organizationName}</p>
                </div>
              </div>
            )}

            {userInfo.deletedAt && (
              <p className="text-xs text-slate-500">
                Deleted on {formatDate(userInfo.deletedAt)}
              </p>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Password Reset Required</p>
              <p className="mt-1">
                For security, a password reset email will be sent to the user.
                They will need to set a new password before logging in.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleRestore}
            disabled={isLoading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Restoring...
              </>
            ) : (
              'Restore & Send Password Reset'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
